import { useCallback, useState } from 'react';
import { cancelInvoice, deleteInvoice } from 'api/inventory/api';
import { notificationDispatcher } from 'components/Notifications/utils';
import { GraniteButton } from 'components/V2/Button/GraniteButton';
import { getQueryClient } from 'query-client';
import { Notification } from 'api/notifications/schema';
import { Modal } from 'components/Modal/Modal';
import Loader from 'components/Loader/Loader';
import { useModal } from 'hooks/useModal';
import showToast from 'components/Toast/Toast';

interface ActionButtonsProps {
  status: 'processing' | 'pending action' | string;
  id: string;
}

const ActionButtons = ({ status, id }: ActionButtonsProps) => {
  const queryClient = getQueryClient();
  const modalProps = useModal();
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsLoading(true);
      try {
        await cancelInvoice(id);
        showToast.confirmation({
          message: 'Invoice has been successfully cancelled.',
        });
        queryClient.invalidateQueries(['upload-widget-table']);
      } finally {
        setIsLoading(false);
      }
    },
    [id, queryClient],
  );

  const handleReview = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      notificationDispatcher.emit('set-invoice-id', {
        event: { id },
      } as Notification);
    },
    [id],
  );

  const handleDelete = useCallback(async () => {
    setIsLoading(true);
    try {
      await deleteInvoice(id);
      showToast.confirmation({
        message: 'Invoice has been successfully deleted.',
      });
      queryClient.invalidateQueries(['upload-widget-table']);
      modalProps.close();
    } finally {
      setIsLoading(false);
    }
  }, [id, queryClient, modalProps]);

  const openDeleteModal = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      modalProps.open();
    },
    [modalProps],
  );

  if (status === 'processing') {
    return (
      <GraniteButton
        variant="secondary"
        size="small"
        onClick={handleCancel}
        disabled={isLoading}
      >
        Cancel
      </GraniteButton>
    );
  }

  if (status === 'pending action') {
    return (
      <>
        <div className="flex gap-3">
          <GraniteButton variant="primary" size="small" onClick={handleReview}>
            Review
          </GraniteButton>
          <GraniteButton
            variant="destructive"
            size="small"
            onClick={openDeleteModal}
          >
            Delete
          </GraniteButton>
        </div>
        <DeleteInvoiceConfirmationDialog
          {...modalProps}
          onConfirmation={handleDelete}
          isLoading={isLoading}
        />
      </>
    );
  }

  return null;
};

export default ActionButtons;

const DeleteInvoiceConfirmationDialog = ({
  isOpen,
  close,
  onConfirmation,
  isLoading,
}: {
  isOpen: boolean;
  close: () => void;
  onConfirmation: () => void;
  isLoading: boolean;
}) => (
  <Modal isVisible={isOpen} close={close} className="max-w-[624px]">
    <div className="space-y-12 rounded-t-lg bg-background-base-surface-2 p-8">
      <div className="space-y-2">
        <h1 className="text-content-base-default">
          Are you sure you want to delete this invoice?
        </h1>
        <p className="font-bold text-content-base-subdued">
          Are you sure you want to delete this invoice? This action cannot be
          undone.
        </p>
      </div>
      <div className="flex gap-5">
        <GraniteButton size="large" variant="secondary" onClick={close}>
          Cancel
        </GraniteButton>
        <GraniteButton
          size="large"
          variant="destructive"
          disabled={isLoading}
          onClick={onConfirmation}
        >
          Yes, delete invoice {isLoading && <Loader width="w-4" height="h-4" />}
        </GraniteButton>
      </div>
    </div>
  </Modal>
);
